var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { orderBy, omit } from "lodash-es";
import { IntervalTree } from "node-interval-tree";
import { parseISO } from "date-fns";
import { findGaps } from "./utils/find-gaps.js";
import { extractIntervals } from "./utils/extract-intervals.js";
import { nanoid } from "nanoid";
import { isRepeatActive, getIntervalsFromRepeat } from "@source-digital/experience-utils";
class ActivationInstanceInterval {
  constructor(data) {
    __publicField(this, "data");
    __publicField(this, "low");
    __publicField(this, "high");
    var _a, _b;
    this.data = data;
    this.low = ((_a = data.instance.when) == null ? void 0 : _a.start) ?? 0;
    this.high = ((_b = data.instance.when) == null ? void 0 : _b.end) ?? 0;
  }
}
class ActivationTimeline {
  constructor(items, options = {}) {
    __publicField(this, "DEFAULT_DURATION", 1e3 * 60 * 60 * 2);
    __publicField(this, "intervalTree", new IntervalTree());
    __publicField(this, "duration", this.DEFAULT_DURATION);
    __publicField(this, "generateId");
    __publicField(this, "rawItems", []);
    __publicField(this, "data");
    this.resetActivationStore();
    const { generateId = nanoid, duration } = options;
    this.generateId = generateId;
    if (duration != null)
      this.duration = duration;
    this.set(items);
  }
  get items() {
    return this.data.items;
  }
  clear() {
    this.intervalTree = new IntervalTree();
    this.rawItems = [];
    this.resetActivationStore();
  }
  resetActivationStore() {
    this.data = {
      items: [],
      repeat: [],
      static: [],
      unknown: []
    };
  }
  set(items) {
    const sorted = [...items.sort((a) => a.instance.fill != null ? 1 : -1)];
    for (const item of sorted) {
      this.rawItems.push(item);
      this._insert(item);
    }
  }
  reevaluate() {
    this.intervalTree = new IntervalTree();
    this.resetActivationStore();
    for (const item of this.rawItems) {
      this._insert(item);
    }
  }
  /**
   * TODO: When new non-fill items are added, should we recompute Items injected by dynamicFill?
   * Until above is resolved/implemented, not recommended to be used from outside of this class. Keeping it private.
   * @param item
   * @returns
   */
  _insert(item) {
    if (item.instance.active === false)
      return;
    this.data.items.push(item);
    if (item.instance.repeat != null) {
      this.data.repeat.push(item);
    } else if (item.instance.static === true) {
      this.data.static.push(item);
    } else if (item.instance.when != null) {
      this.intervalTree.insert(new ActivationInstanceInterval(item));
    } else if (item.instance.fill != null) {
      this.dynamicFill(item);
    } else {
      this.data.unknown.push(item);
    }
  }
  _isItemActive(event) {
    var _a, _b, _c, _d, _e;
    if (typeof ((_a = event == null ? void 0 : event.instance) == null ? void 0 : _a.active) === "boolean") {
      return (_b = event == null ? void 0 : event.instance) == null ? void 0 : _b.active;
    }
    if (((_c = event == null ? void 0 : event.instance) == null ? void 0 : _c.active) == null || typeof ((_d = event == null ? void 0 : event.instance) == null ? void 0 : _d.active) !== "object") {
      return true;
    }
    const { since, until } = ((_e = event == null ? void 0 : event.instance) == null ? void 0 : _e.active) ?? {};
    if (since == null && until == null) {
      return false;
    }
    const now = Date.now();
    if (since != null && parseISO(since).getTime() > now) {
      return false;
    }
    if (until != null && parseISO(until).getTime() < now) {
      return false;
    }
    return true;
  }
  _sort(items) {
    return orderBy(items, [(i) => {
      var _a;
      return ((_a = i.instance) == null ? void 0 : _a.priority) != null;
    }, (i) => {
      var _a;
      return (_a = i.instance) == null ? void 0 : _a.priority;
    }, (i) => {
      var _a, _b;
      return ((_b = (_a = i.instance) == null ? void 0 : _a.when) == null ? void 0 : _b.start) != null;
    }], ["desc", "desc", "desc"]);
  }
  /**
   * injects fill items into the timeline. See {@link ActivationTimeline.findGaps} for more details.
   * @param item
   * @returns
   */
  dynamicFill(item) {
    if (item.instance.fill == null)
      return;
    const { concurrentAllowed = 0, times, duration = 8e3, interval = 0, concurrentCount = 1 } = item.instance.fill;
    if (times === 0)
      return;
    const instances = [];
    let remaining = times ?? Infinity;
    for (const gap of this.findGaps(duration, interval, concurrentAllowed)) {
      const instanceNum = Math.min(remaining, concurrentCount);
      const instanceId = item.instance.id ?? this.generateId();
      Array.from({ length: instanceNum }).forEach((v, i) => {
        instances.push({
          ...item,
          instance: {
            ...omit(item.instance, ["fill"]),
            id: `${instanceId}__${i}`,
            when: {
              start: gap.low,
              end: gap.high
            }
          }
        });
      });
      remaining -= instanceNum;
      if (remaining <= 0)
        break;
    }
    instances.forEach((i) => {
      this._insert(i);
    });
    if (times != null && times > instances.length) {
      console.warn("Could not allocate enough space for fill item", item);
    }
  }
  /**
   * @param time
   * @returns instances that are active at the given time
   */
  getAt(time) {
    const intervalItems = this.intervalTree.search(time, time).map((i) => i.data);
    const repeatItems = this.data.repeat.filter((i) => i.instance.repeat != null && isRepeatActive(time, i.instance.repeat));
    const staticItems = this.data.static;
    const items = [...intervalItems, ...repeatItems, ...staticItems].filter((i) => this._isItemActive(i));
    return this._sort(items);
  }
  /**
   * Detects gaps in the timeline
   * Note that this is rather computation heavy, so it should not be used in a render loop
   * Don't forget to check if the item is active before considering it an active interval
   * Limitation: date based active value is only considered at the time this function is called.
   * @param duration desired duration of the gap in milliseconds
   * @param interval interval between duration in milliseconds
   * @param concurrentAllowed allowed concurrent items in the gap
   * @param totalDuration total duration in milliseconds. Often the length of the timeline. Default is 2 hours. Not ideal, but needed to consider repeated items
   * @returns Array of found gaps
   */
  *findGaps(duration, interval, concurrentAllowed = 0) {
    const totalDuration = this.duration;
    const _concurrentAllowed = concurrentAllowed - this.data.static.filter((i) => this._isItemActive(i)).length;
    if (_concurrentAllowed < 0) {
      return;
    }
    const intervals = [];
    const records = Array.from(this.intervalTree.inOrder()).filter((i) => this._isItemActive(i.data));
    records.forEach((i) => intervals.push({ low: i.low, high: i.high }));
    for (const item of this.data.repeat) {
      if (!this._isItemActive(item) || item.instance.repeat == null)
        continue;
      getIntervalsFromRepeat({ ...item.instance.repeat, end: totalDuration }).forEach((i) => intervals.push({ low: i[0], high: i[1] }));
    }
    const gaps = Array.from(
      findGaps(
        [
          ...intervals,
          // expanding video duration by interval (time btwn clips) to be able to detect interval to start at video start and end
          { low: -interval, high: totalDuration + interval }
        ],
        // incrementing concurrentAllowed by 1 to allow for the video interval to overlap
        _concurrentAllowed + 1,
        // find gaps that are at least interval + duration + interval long
        duration + 2 * interval,
        // start time
        -interval
      )
    ).map(({ low, high }) => ({ low: low + interval, high }));
    for (const extracted of extractIntervals(gaps, duration + interval, 0)) {
      yield {
        low: extracted.low,
        high: extracted.high - interval
      };
    }
  }
}
export {
  ActivationTimeline
};
